import { btn, card } from "@/components/ds/styles";
import { ClosingBand, type ClosingPhoto } from "@/components/ds/closing-band";
import reading from "@/assets/dr-sara-reading-circle.jpg.asset.json";
import { useLongPress } from "@/lib/use-long-press";
import { Link } from "@tanstack/react-router";
import { Bookmark } from "lucide-react";
import { TONE_CLASS, ageRange, rakizaTone } from "@/lib/home-format";
import { ltr } from "@/lib/blog/age-bands";
import { primaryPillar } from "@/lib/blog/pillars";
import { firstSentence, minutesPhrase } from "@/lib/blog/text";
import { formatUsdShort } from "@/lib/payments/currency";

export type BlogPost = {
  id: string;
  slug: string;
  title_ar: string;
  tldr_ar: string | null;
  og_image: string | null;
  cover_image?: string | null;
  image_alt: string | null;
  reading_min: number | null;
  age_band: string[] | null;
  rakiza: string[] | null;
  published_at: string | null;
  related_post_ids?: string[] | null;
};

export { eyebrow } from "@/components/ds/styles";
export const primaryBtn = btn.primary;
export const secondaryBtn = btn.secondary;

export function postMeta(p: Pick<BlogPost, "reading_min" | "age_band">): string {
  const parts: string[] = [];
  if (p.reading_min) parts.push(minutesPhrase(p.reading_min));
  const age = ageRange(p.age_band);
  if (age) parts.push(`${ltr(age)} سنوات`);
  return parts.join(" · ");
}

export function PillarPill({ rakiza, className = "" }: { rakiza: unknown; className?: string }) {
  const label = primaryPillar(rakiza);
  return (
    <span
      className={`inline-flex items-center rounded-ds-pill px-3 py-1 font-display text-[13px] font-bold ${TONE_CLASS[rakizaTone(label)]} ${className}`}
    >
      {label}
    </span>
  );
}

export function BookmarkButton({ saved, onToggle }: { saved: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label="حفظ المقال"
      aria-pressed={saved}
      onClick={onToggle}
      className={`grid h-12 w-12 shrink-0 place-items-center rounded-full border transition-colors duration-ds-fast ${
        saved ? "border-amber-600 bg-amber-100 text-amber-800" : "border-hairline-default bg-surface text-brown-700 hover:bg-cream-100"
      }`}
    >
      <Bookmark className="h-5 w-5" strokeWidth={1.75} fill={saved ? "currentColor" : "none"} aria-hidden />
    </button>
  );
}

export function PostCard({ post, saved, onToggleSave }: { post: BlogPost; saved: boolean; onToggleSave: () => void }) {
  // Long-press (touch) toggles saved, with a light haptic; taps still open the article.
  const press = useLongPress(() => {
    try { navigator.vibrate?.(10); } catch { /* unsupported */ }
    onToggleSave();
  });
  return (
    <article {...press} className={`${card.interactive} grid select-none grid-cols-[92px_1fr] gap-4 p-4 [-webkit-touch-callout:none]`}>
      <Link to="/blog/$slug" params={{ slug: post.slug }} tabIndex={-1} aria-hidden className="block">
        {post.og_image ? (
          <img
            src={post.og_image}
            alt={post.title_ar}
            width={92}
            height={112}
            loading="lazy"
            decoding="async"
            className="h-[112px] w-[92px] rounded-ds-md object-cover"
          />
        ) : (
          <div className="h-[112px] w-[92px] rounded-ds-md bg-cream-100" />
        )}
      </Link>
      <div className="flex min-w-0 flex-col">
        <Link to="/blog/$slug" params={{ slug: post.slug }} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {post.tldr_ar && <p className="text-[16px] leading-[1.65] text-text-primary">{firstSentence(post.tldr_ar)}</p>}
          <h3 className="mt-1 font-display text-[15px] font-bold text-text-secondary">{post.title_ar}</h3>
        </Link>
        <div className="mt-auto flex items-center gap-2 pt-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <PillarPill rakiza={post.rakiza} />
            <span className="text-[13px] text-text-secondary">{postMeta(post)}</span>
          </div>
          <BookmarkButton saved={saved} onToggle={onToggleSave} />
        </div>
      </div>
    </article>
  );
}

/** Brown closing band shared by the index and every article. */
export function BrownCtaBand({
  eyebrowText,
  heading,
  line,
  price,
  packagePrice,
  headingSize = "text-[30px]",
  photo = { src: reading.url, alt: "د. سارة تقرأ كتاب عن تفكير الطفل", width: 520, height: 520 },
}: {
  eyebrowText?: string;
  heading: string;
  line: string;
  price: number | null;
  packagePrice: number | null;
  headingSize?: string;
  photo?: ClosingPhoto;
}) {
  return (
    <ClosingBand
      photo={photo}
      as="section"
      className="mx-3 mt-14 md:mx-auto md:max-w-[52rem]"
      titleClass={headingSize}
      eyebrow={eyebrowText}
      title={heading}
      line={line}
      price={price != null ? formatUsdShort(price) : null}
      priceNote={`للجلسة${packagePrice != null ? ` · 4 جلسات بـ ${ltr(formatUsdShort(packagePrice))}` : ""}`}
    >
      <Link to="/book" className={`${btn.inversePrimary} w-full`}>
        حجز استشارة ←
      </Link>
    </ClosingBand>
  );
}
